import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { usePollingContext } from 'src/providers/PollingContextProvider';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { getTrafficV2Treatment } from 'src/utils/app';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { useOptimisticPendingSegmentsStore } from 'src/zustandStores/Atlas/useOptimisticPendingSegmentsStore';

const PROCESSING_SEGMENTS_POLLING_KEY = 'PROCESSING_SEGMENTS_POLL';

export const useFetchAndPollingSegments = () => {
  const { divisionId } = useAppSelector((state) => state.user.session);
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { showSnackbar } = useSnackbar();
  const { startPolling, stopPolling } = usePollingContext();
  const { beaconNavigator } = useBeaconNavigator();
  const [processingSegments, setProcessingSegments] = useState<string[]>([]);
  const previousProcessingSegmentsRef = useRef<string[]>([]);

  // Zustand store for optimistic pending segments
  const { getOptimisticPendingSegmentIds, removeOptimisticPendingSegment } = useOptimisticPendingSegmentsStore();

  const fetchProcessingSegments = useCallback(async () => {
    try {
      const response = await axios.post('/api/atlas/GetProcessingSegments', {
        divisionId,
        retailerId
      });

      if (response.data && Array.isArray(response.data)) {
        const currentProcessingSegments = response.data;
        const previous = previousProcessingSegmentsRef.current;
        const optimisticPending = getOptimisticPendingSegmentIds();

        // Find segments that completed processing (were in previous but not in current)
        const completedSegments = previous.filter((segmentId) => !currentProcessingSegments.includes(segmentId));

        // Also check for optimistically pending segments that are not in the API response
        // This means they either completed or haven't started processing yet
        const optimisticCompleted = optimisticPending.filter(
          (segmentId) => !currentProcessingSegments.includes(segmentId) && previous.includes(segmentId)
        );

        const allCompletedSegments = [...new Set([...completedSegments, ...optimisticCompleted])];

        if (allCompletedSegments.length > 0) {
          allCompletedSegments.forEach((segmentId) => {
            removeOptimisticPendingSegment(segmentId);
          });

          showSnackbar({
            type: 'success',
            showButton: true,
            buttonProps: {
              text: 'View Segment',
              onClick: () => {
                beaconNavigator.setPathname(`/segment/${allCompletedSegments[0]}`).addSearchParams({
                  tab: ATLAS_TABS.SUMMARY_V2,
                  subtab: ATLAS_SUBTABS.KEY_METRICS
                });

                beaconNavigator.go();
              }
            },
            message: 'Your segment was successfully created.',
            timeout: null
          });
        }

        setProcessingSegments(currentProcessingSegments);

        // Update previous ref to include both API and optimistic segments
        const mergedPending = [...new Set([...currentProcessingSegments, ...optimisticPending])];
        previousProcessingSegmentsRef.current = mergedPending;
      }
    } catch (error) {
      console.error('Error fetching processing segments:', error);
    }
  }, [
    divisionId,
    retailerId,
    showSnackbar,
    beaconNavigator,
    getOptimisticPendingSegmentIds,
    removeOptimisticPendingSegment
  ]);

  useEffect(() => {
    if (divisionId && retailerId && (getTrafficV2Treatment() === 'both' || getTrafficV2Treatment() === 'on')) {
      try {
        startPolling(PROCESSING_SEGMENTS_POLLING_KEY, fetchProcessingSegments, {
          interval: 5000,
          leading: true,
          shouldThrow: false
        });
      } catch (e) {
        console.error('Polling already started');
      }
    }

    return () => stopPolling(PROCESSING_SEGMENTS_POLLING_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionId, retailerId]);

  // Merge API processing segments with optimistic pending segments
  const mergedProcessingSegments = useMemo(() => {
    const optimisticPending = getOptimisticPendingSegmentIds();
    return [...new Set([...processingSegments, ...optimisticPending])];
  }, [processingSegments, getOptimisticPendingSegmentIds]);

  return {
    processingSegments: mergedProcessingSegments
  };
};
